import { afterPageView } from "@/lib/ga";
import { logCtaClick, type CtaClickInput } from "@/lib/cta-clicks.functions";

function store(row: CtaClickInput): void {
  void logCtaClick({ data: row }).then(undefined, () => undefined);
}
import { attributionProps, captureAttribution, getAttribution } from "@/lib/attribution";

type ClickRecord = {
  cta_id: string;
  label: string;
  location: string;
  /** Ścieżka strony, z której pochodzi kliknięcie (domyślnie bieżąca). */
  path?: string;
};

/** Polskie kategorie ofert dla zdarzenia cta_click. */
export type OfferType = "prelekcja" | "mastermind" | "konsultacja" | "inne";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    plausible?: (event: string, opts?: { props?: Record<string, string> }) => void;
  }
}

/**
 * Oznacza element jako istotne biznesowo CTA. Tylko elementy z tymi atrybutami
 * wysyłają cta_click do GA4 (przez globalny nasłuch, dokładnie raz na kliknięcie).
 * cta_name to stała nazwa, niezależna od tekstu na przycisku.
 */
export function cta(
  name: string,
  location: string,
  offer: OfferType,
  destination?: string,
): Record<string, string> {
  const attrs: Record<string, string> = {
    "data-cta-name": name,
    "data-cta-location": location,
    "data-offer-type": offer,
  };
  if (destination) attrs["data-destination"] = destination;
  return attrs;
}

/**
 * Oznacza link e-mail (mailto:) dla zdarzenia contact_click.
 * Nie ustawia data-cta-name, więc to kliknięcie nie wysyła cta_click.
 */
export function contactLink(location: string, offer: OfferType = "inne"): Record<string, string> {
  return { "data-contact-location": location, "data-offer-type": offer };
}

/** Zapis kliknięcia w wewnętrznej bazie strony (tabela cta_clicks), bez zmian względem wcześniej. */
function recordClick({ cta_id: ctaId, label, location, path: pathOverride }: ClickRecord): void {
  const path = pathOverride ?? window.location.pathname;
  const attribution = getAttribution();

  try {
    window.plausible?.("CTA Click", {
      props: { id: ctaId, label, location, path, ...attributionProps() },
    });
  } catch {
    // analityka nigdy nie może blokować nawigacji
  }

  store({
      cta_id: ctaId,
      label,
      location,
      path,
      referrer: document.referrer || null,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      click_id: attribution.click_id,
      landing_page: attribution.landing_page,
  });
}

/** Jedno zdarzenie cta_click wysłane do GA4 bezpośrednio ze strony (gtag.js). */
function sendCtaClick(params: {
  cta_name: string;
  cta_text: string;
  cta_location: string;
  offer_type: string;
  destination: string;
  page_path: string;
}): void {
  try {
    window.gtag?.("event", "cta_click", params);
  } catch {
    // analityka nigdy nie może blokować nawigacji
  }
}

/**
 * Rejestruje konwersję (wysłany formularz rezerwacji lub kontaktu) razem
 * z pełną atrybucją kampanii. Zapis w bazie trafia do tej samej tabeli
 * co kliknięcia, z prefiksem "konwersja:" w polu cta_id.
 */
export type LeadType = "prelekcja" | "mastermind" | "konsultacja" | "kontakt" | "inne";

/** Temat z formularza kontaktowego -> lead_type dla GA4. */
export function leadTypeFromTopic(topic: string): LeadType {
  if (topic.startsWith("Mastermind")) return "mastermind";
  if (topic.startsWith("Konsultacje")) return "konsultacja";
  if (topic.startsWith("Wystąpienie")) return "prelekcja";
  if (topic.startsWith("Inny")) return "inne";
  return "kontakt";
}

export function trackConversion(
  conversionId: "rezerwacja-wyslana" | "kontakt-wyslany",
  label: string,
  details: Record<string, string> = {},
  lead?: { leadType: LeadType; formName: string },
): void {
  if (typeof window === "undefined") return;

  const path = window.location.pathname;
  const attribution = getAttribution();

  try {
    // GA4 działa przez GTM: jedno zdarzenie w dataLayer, bez żadnych danych osobowych.
    if (lead) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "generate_lead",
        lead_type: lead.leadType,
        form_name: lead.formName,
        page_path: path,
      });
    }
    window.plausible?.("Conversion", {
      props: { id: conversionId, label, path, ...attributionProps() },
    });
  } catch {
    // analityka nie może blokować potwierdzenia formularza
  }

  store({
      cta_id: `konwersja:${conversionId}`,
      label,
      location: details['location'] ?? "formularz",
      path,
      referrer: document.referrer || null,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      click_id: attribution.click_id,
      landing_page: attribution.landing_page,
  });
}

/** form_interaction_start wysłane już dla tych formularzy (raz na formularz na instancję strony). */
const formInteractionSent = new Set<string>();

/**
 * Pierwsza rzeczywista interakcja z formularzem: jedno własne zdarzenie
 * form_interaction_start wysyłane bezpośrednio do GA4 przez istniejący moduł
 * gtag (bez nowego tagu w GTM), z form_name i page_path, bez danych osobowych.
 * Nie wysyłamy własnego form_start, żeby nie dublować automatycznego pomiaru GA4.
 * Na hostach podglądu gtag nie istnieje (GA4 jest wyłączone), więc to wywołanie
 * jest tam bezpiecznym no-op.
 */
export function trackFormInteractionStart(formName: string): void {
  if (typeof window === "undefined") return;
  if (formInteractionSent.has(formName)) return;
  formInteractionSent.add(formName);
  try {
    window.gtag?.("event", "form_interaction_start", {
      form_name: formName,
      page_path: window.location.pathname,
    });
  } catch {
    // analityka nie może blokować formularza
  }
}

/** "Poznaj mastermind" -> "poznaj-mastermind" */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** Najbliższa sekcja z id, żeby wiedzieć skąd pochodzi kliknięcie. */
function sectionOf(el: Element): string {
  const section = el.closest<HTMLElement>("section[id], header, footer, nav, main");
  if (!section) return "strona";
  if (section.id) return section.id;
  return section.tagName.toLowerCase();
}

/**
 * Globalny nasłuch kliknięć: każde kliknięcie trafia do wewnętrznej bazy,
 * a do GA4 jako cta_click wyłącznie elementy oznaczone helperem cta().
 */
export function initClickTracking(): () => void {
  if (typeof document === "undefined") return () => undefined;

  // atrybucja wizyty: przy starcie i po każdej zmianie adresu (SPA)
  captureAttribution();
  const onLocationChange = () => captureAttribution();
  window.addEventListener("popstate", onLocationChange);
  const originalPushState = history.pushState.bind(history);
  history.pushState = function patchedPushState(...args: Parameters<History["pushState"]>) {
    originalPushState(...args);
    onLocationChange();
  };

  // ścieżka sprzed nawigacji, żeby zdarzenie trafiło na właściwą stronę
  let pathAtPress = window.location.pathname;
  const press = () => {
    pathAtPress = window.location.pathname;
  };

  const handler = (event: MouseEvent) => {
    const target = event.target as Element | null;
    const el = target?.closest?.("a, button");
    if (!el) return;

    const label = (el.getAttribute("aria-label") || el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120);
    const href = el.getAttribute("href") ?? "";
    const ctaName = el.getAttribute("data-cta-name");

    // 1) wewnętrzna baza: każde kliknięcie, jak dotąd
    if (label) {
      const ctaId = ctaName || el.getAttribute("data-cta-id") || slugify(label) || slugify(href) || "bez-etykiety";
      recordClick({ cta_id: ctaId, label, location: `${pathAtPress}, ${sectionOf(el)}`, path: pathAtPress });
    }

    // 2a) GA4: bezpośredni kontakt e-mail, bez adresu e-mail w parametrach
    if (href.toLowerCase().startsWith("mailto:")) {
      try {
        window.gtag?.("event", "contact_click", {
          contact_type: "email",
          page_path: pathAtPress,
          cta_location: el.getAttribute("data-contact-location") || sectionOf(el),
          offer_type: el.getAttribute("data-offer-type") || "inne",
        });
      } catch {
        // analityka nigdy nie może blokować nawigacji
      }
      return;
    }

    // 2b) GA4: tylko elementy oznaczone jako CTA, jedno zdarzenie na kliknięcie
    if (ctaName) {
      sendCtaClick({
        cta_name: ctaName,
        cta_text: el.getAttribute("data-cta-text") || (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120),
        cta_location: el.getAttribute("data-cta-location") || sectionOf(el),
        offer_type: el.getAttribute("data-offer-type") || "inne",
        destination: el.getAttribute("data-destination") || href,
        page_path: pathAtPress,
      });
    }
  };

  document.addEventListener("pointerdown", press, { capture: true });
  document.addEventListener("keydown", press, { capture: true });
  document.addEventListener("click", handler, { capture: true });
  return () => {
    window.removeEventListener("popstate", onLocationChange);
    history.pushState = originalPushState;
    document.removeEventListener("pointerdown", press, { capture: true });
    document.removeEventListener("keydown", press, { capture: true });
    document.removeEventListener("click", handler, { capture: true });
  };
}

/** Podstrony ofert objęte zdarzeniem offer_view. /rezerwacja celowo pominięta. */
const OFFER_PAGES: Record<string, OfferType> = {
  "/wystapienia": "prelekcja",
  "/mastermind": "mastermind",
  "/mastermind-dla-przedsiebiorcow": "mastermind",
  "/konsultacje": "konsultacja",
};

function normalizePath(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

/** Poprzednia podstrona w obrębie witryny z document.referrer (tylko ścieżka, bez parametrów). */
function internalReferrerPath(): string {
  try {
    if (!document.referrer) return "";
    const ref = new URL(document.referrer);
    return ref.origin === window.location.origin ? normalizePath(ref.pathname) : "";
  } catch {
    return "";
  }
}

/**
 * offer_view: jedno zdarzenie na jedno wejście na podstronę oferty, wysyłane
 * bezpośrednio do GA4 (gtag.js). Nie reaguje na scroll, kliknięcia, zmiany #kotwicy
 * ani ponowne renderowanie; nowe zdarzenie tylko przy zmianie ścieżki.
 */
export function initOfferViews(): () => void {
  if (typeof window === "undefined") return () => undefined;

  let currentPath = normalizePath(window.location.pathname);
  let previousPath = internalReferrerPath();
  let cancelled = false;
  let cancelPending: () => void = () => undefined;

  const send = (path: string, source: string) => {
    const offer = OFFER_PAGES[path];
    if (!offer) return;
    const params: Record<string, string> = { offer_type: offer, page_path: path };
    if (source && source !== path) params["source_page"] = source;
    // offer_view dopiero po page_view tej samej podstrony (page_view → offer_view)
    cancelPending();
    cancelPending = afterPageView(path, () => {
      if (cancelled || normalizePath(window.location.pathname) !== path) return;
      try {
        window.gtag?.("event", "offer_view", params);
      } catch {
        // analityka nie może blokować strony
      }
    });
  };

  send(currentPath, previousPath);

  const onChange = () => {
    const next = normalizePath(window.location.pathname);
    if (next === currentPath) return;
    previousPath = currentPath;
    currentPath = next;
    send(currentPath, previousPath);
  };

  window.addEventListener("popstate", onChange);
  const originalPushState = history.pushState.bind(history);
  history.pushState = function patchedPushState(...args: Parameters<History["pushState"]>) {
    originalPushState(...args);
    onChange();
  };

  return () => {
    cancelled = true;
    cancelPending();
    window.removeEventListener("popstate", onChange);
    history.pushState = originalPushState;
  };
}
